import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Building2, Users, CheckCircle2, UserPlus, FileText, Send } from "lucide-react";
import Navbar from "../components/Navbar";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1560264280-88b68371db39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFscyUyMG9mZmljZXxlbnwwfHx8fDE3ODYxMTYxMjR8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState({ jobs: 0, companies: 0, candidates: 0 });

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const companyFeatures = [t("landing.f1"), t("landing.f2"), t("landing.f3"), t("landing.f4")];
  const candidateFeatures = [t("landing.c1"), t("landing.c2"), t("landing.c3"), t("landing.c4")];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2 lg:py-28">
          <div>
            <span data-testid="hero-badge" className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-yellow-300 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-900">
              <Briefcase className="h-3.5 w-3.5" strokeWidth={2.5} /> {t("landing.badge")}
            </span>
            <h1 className="mt-6 font-heading text-5xl font-bold leading-[1.05] tracking-tight text-slate-900 md:text-6xl">
              {t("landing.heroTitle")}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to={user ? "/jobs" : "/register?role=candidate"}
                data-testid="hero-cta-candidate"
                className="group inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 px-6 py-3 font-heading text-base font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-1 hover:bg-sky-500 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]"
              >
                {t("landing.ctaCandidate")}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
              </Link>
              <Link
                to={user ? "/company" : "/register?role=company"}
                data-testid="hero-cta-company"
                className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-6 py-3 font-heading text-base font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]"
              >
                {t("landing.ctaCompany")}
              </Link>
            </div>
            <div className="mt-12 flex gap-10">
              <div data-testid="stat-jobs"><p className="font-heading text-3xl font-bold text-slate-900">{stats.jobs}</p><p className="text-sm text-slate-500">{t("landing.statsJobs")}</p></div>
              <div data-testid="stat-companies"><p className="font-heading text-3xl font-bold text-slate-900">{stats.companies}</p><p className="text-sm text-slate-500">{t("landing.statsCompanies")}</p></div>
              <div data-testid="stat-candidates"><p className="font-heading text-3xl font-bold text-slate-900">{stats.candidates}</p><p className="text-sm text-slate-500">{t("landing.statsCandidates")}</p></div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -right-6 -top-6 h-full w-full rounded-md border-2 border-slate-900 bg-sky-200" aria-hidden="true" />
            <img
              src={HERO_IMG}
              alt="Professionals collaborating in an office"
              className="relative z-10 aspect-[4/3] w-full rounded-md border-2 border-slate-900 object-cover"
            />
            <Link
              to="/jobs"
              data-testid="hero-jobs-badge"
              className="absolute -bottom-5 left-6 z-20 flex items-center gap-3 rounded-md border-2 border-slate-900 bg-white px-5 py-3 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] transition-transform duration-200 hover:-translate-y-1"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-400">
                <Briefcase className="h-4 w-4 text-slate-900" strokeWidth={2.5} />
              </span>
              <span className="text-sm font-bold text-slate-900">{stats.jobs} {t("landing.jobsAvailable")}</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="border-y-2 border-slate-900 bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{t("landing.featuresTitle")}</h2>
          <p className="mt-3 text-base text-slate-600 md:text-lg">{t("landing.featuresSubtitle")}</p>

          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <div className="rounded-md border-2 border-slate-900 bg-sky-50 p-8 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]" data-testid="feature-companies">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-slate-900 bg-sky-400">
                  <Building2 className="h-5 w-5 text-slate-900" strokeWidth={2} />
                </span>
                <h3 className="font-heading text-xl font-bold text-slate-900 md:text-2xl">{t("landing.forCompanies")}</h3>
              </div>
              <ul className="mt-6 space-y-3">
                {companyFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" strokeWidth={2.5} /> {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-md border-2 border-slate-900 bg-yellow-50 p-8 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]" data-testid="feature-candidates">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-slate-900 bg-yellow-300">
                  <Users className="h-5 w-5 text-slate-900" strokeWidth={2} />
                </span>
                <h3 className="font-heading text-xl font-bold text-slate-900 md:text-2xl">{t("landing.forCandidates")}</h3>
              </div>
              <ul className="mt-6 space-y-3">
                {candidateFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" strokeWidth={2.5} /> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{t("landing.howTitle")}</h2>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              { icon: UserPlus, title: t("landing.step1t"), desc: t("landing.step1d"), n: "01" },
              { icon: FileText, title: t("landing.step2t"), desc: t("landing.step2d"), n: "02" },
              { icon: Send, title: t("landing.step3t"), desc: t("landing.step3d"), n: "03" },
            ].map((s) => (
              <div key={s.n} className="relative rounded-md border-2 border-slate-900 bg-white p-8 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]">
                <span className="absolute right-5 top-5 font-heading text-4xl font-bold text-sky-200">{s.n}</span>
                <span className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-slate-900 bg-sky-100">
                  <s.icon className="h-5 w-5 text-slate-900" strokeWidth={2} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t-2 border-slate-900 bg-sky-400 py-20">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">{t("landing.ctaTitle")}</h2>
          <p className="mt-3 text-base font-medium text-slate-800 md:text-lg">{t("landing.ctaSubtitle")}</p>
          <Link
            to="/register"
            data-testid="cta-register-button"
            className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-slate-900 bg-white px-8 py-3.5 font-heading text-base font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,1)]"
          >
            {t("auth.registerBtn")} <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      <footer className="border-t-2 border-slate-900 bg-slate-900 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row">
          <img src="/assets/logo.png" alt="Global Line Services" className="h-8 w-auto rounded bg-white px-2 py-1" />
          <p className="text-sm text-slate-400">{t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}
